import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NbDialogRef, NbCardModule, NbButtonModule, NbPopoverModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { KPI } from '../../../@core/api';
import { AssetsService, ConfigService, InitiativesService, InvestmentsService, LayoutService, ScopeService, SpreadsheetColumnsService, UsersService, } from '../../../@core/utils';
import { AgGridAssetComponent } from '../ag-grid-asset/ag-grid-asset.component';
import { InvestmentsSpreadsheetComponent } from '../investments-spreadsheet/investments-spreadsheet.component';
import { InitiativesSpreadsheetComponent } from '../initiatives-spreadsheet/initiatives-spreadsheet.component';
import { InitiativeSheetComponent } from '../initiative-sheet/initiative-sheet.component';
import { InvestmentSheetComponent } from '../investment-sheet/investment-sheet.component';
import { AssetSheetComponent } from '../asset-sheet/asset-sheet.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass, NgStyle } from '@angular/common';
import { MappedInvestmentDocuments } from '../../../@core/utils/investments.service';
import { GROUP_PERMISSION_ACCESS_AUDIT } from '../../../@core/api/models';

@Component({
  selector: 'app-sheet-dialog',
  templateUrl: './sheet-dialog.component.html',
  styleUrls: ['./sheet-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    NgStyle,
    NbButtonModule,
    FontAwesomeModule,
    NbPopoverModule,
    AssetSheetComponent,
    InvestmentSheetComponent,
    InitiativeSheetComponent,
    InitiativesSpreadsheetComponent,
    InvestmentsSpreadsheetComponent,
    forwardRef(() => AgGridAssetComponent),
    TranslateModule,
],
})
export class SheetDialogComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];

  @Input() public title: string = '';
  @Input() public deleteFilter: boolean = true;

  @Input() public assetId: number;
  @Input() public investmentId: number;
  @Input() public initiativeId: number;
  @Input() public minimizedView: boolean = true;

  @Input() public investmentsList: boolean;
  @Input() public investmentsListScenario: KPI.InvestmentScenario;
  @Input() public investmentsListColumns: string[];
  @Input() public investmentsListFilter: { [key: string]: string[] | boolean[] | number[] };
  @Input() public applyPeriod: boolean = true;

  @Input() public initiativesList: boolean;
  @Input() public initiativesListColumns: string[];
  @Input() public initiativesListScenario: KPI.InvestmentScenario;
  @Input() public initiativesListFilters: { [key: string]: string | boolean | number | any[] };
  @Input() public showScenarii: boolean = false;
  @Input() public isFidjiAct: boolean = false;

  @Input() public assetsList: boolean;
  @Input() public assetsListFilter: { [key: string]: string[] | boolean[] | number[] };

  // Used to know what width should we apply to the dialog
  public sheetDialogNb: number = 0;

  public projectViewToggled: boolean = false;
  public energyEnabled: boolean;
  private asset: KPI.MappedAsset;
  private siteId: number;
  public initiativeSiteId: number;
  public canGoToAudit: boolean;

  constructor(
    private nbDialogRef: NbDialogRef<SheetDialogComponent>,
    public layoutService: LayoutService,
    private router: Router,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private scopeService: ScopeService,
    private investmentService: InvestmentsService,
    private assetsService: AssetsService,
    private initiativesService: InitiativesService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private configService: ConfigService,
    private usersService: UsersService
  ) { }

  public ngOnInit(): void {

    this.layoutService.addSheetDialogNb();
    this.sheetDialogNb = this.layoutService.sheetDialogNb;
    this.subscriptions.push(
      combineLatest([
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT]),
        this.configService.customerConfig$
      ]).subscribe(([accessAllowed, config]) => {
        this.canGoToAudit = accessAllowed && config.can_access_audit_app
      })
    )

    this.nbDialogRef.onClose.subscribe(() => {
      this.layoutService.removeSheetDialogNb();
      this.investmentService.setDisableGlobalPreload(false);
    });

    this.nbDialogRef.onBackdropClick.pipe(take(1)).subscribe(() => this.close());

    this.subscriptions.push(
      this.scopeService.user$.subscribe((user) => {
        this.energyEnabled = user.can_access_energy;
      }),
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
      ).subscribe(() => this.close())
    );
    if (this.assetId) {
      this.subscriptions.push(
        this.assetsService.getAsset(this.assetId).subscribe((asset) => {
          this.asset = asset
        })
      )
    } else if (this.investmentId) {
      this.investmentService.setDisableGlobalPreload(true);
      this.subscriptions.push(
        this.investmentService.getInvestment(this.investmentId).subscribe((investment: MappedInvestmentDocuments) => {
          this.siteId = investment.multi_perimeter.id;
        })
      )
    } else if (this.initiativeId) {
      this.subscriptions.push(
        this.initiativesService.getInitiative(this.initiativeId).subscribe((initiative) => {
          this.initiativeSiteId = initiative.site.id
        })
      )
    }
    this.buildTitle(this.title);
  }

  public isTableView(): boolean {
    return this.investmentsList || this.initiativesList;
  }

  public close(): void {

    if (this.deleteFilter) {
      this.spreadsheetColumnsService._deleteInvestFilters.next(true);
    }

    setTimeout(() => {
      this.nbDialogRef.close();
    }, 250);
  }

  public buildTitle(title: string): void {
    if (title) {
      this.title = title;
    } else if (this.investmentsList) {
      this.title = this.translate.instant('pages.insight.investment.investment_table');
    }
    this.cdRef.detectChanges();
  }

  public goToAudit() {
    if (this.assetId) {
      if (!this.asset.perimeters || !this.asset.id) {
        return alert(this.translate.instant("Configuration error, Link to equipement is broken"))
      }
      this.assetsService.openAuditAssetEditor({
        asset: this.asset,
        siteId: this.asset.perimeters[0].id,
      })
    } else if (this.investmentId) {
      this.investmentService.openAuditInvestEditor({
        investmentId: this.investmentId,
        siteId: this.siteId,
      })
    } else if (this.initiativeId) {
      this.initiativesService.openAuditInitiativeEditor({
        initiativeId: this.initiativeId,
        siteId: this.initiativeSiteId,
      })
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public refresh() {
    if (this.assetId) {
      this.assetsService.refresh$.next(null);
    }
    if (this.investmentId) {
      this.investmentService.reloadInvestment$.next(null);
    }
    if (this.initiativeId) {
      this.initiativesService.refresh$.next(null);
    }
  }
}
